/**
 * Sets a nested value in an object at a specified path, creating intermediate objects if necessary.
 *
 * @param obj - The target object where the value should be set. This object will be **mutated**.
 * @param path - An array of strings representing the path to the property.
 * @param value - The value to set at the specified path.
 *
 * @example
 * const obj: Record<string, any> = {};
 * setNestedValue(obj, ['a', 'b', 'c'], 42);
 * console.log(obj); // Output: { a: { b: { c: 42 } } }
 *
 * @remarks
 * - If any part of the path does not exist in the object, it will be created as an empty object.
 * - Overwrites the value at the specified path if it already exists.
 * - Mutates the original object rather than creating a new one.
 */
export const setNestedValue = (obj: Record<string, unknown>, path: string[], value: string): void => {
  path.reduce<Record<string, unknown>>((current, key, index) => {
    if (index === path.length - 1) {
      current[key] = value;
    } else {
      const isNextValueObj = current[key] && typeof current[key] === 'object';
      current[key] = isNextValueObj ? current[key] : {};
    }
    return current[key] as Record<string, unknown>;
  }, obj);
};
